import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import { EAmplitudeScale, EFrequencyScale } from '@web/entities/measurement'
import { EUnitType } from '@web/entities/measurement/model/enums/unit.enum'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'

import type { IConfigChart, TChartWorkMode } from './types/chart-config.type'
import type { IStrobesResult } from './types/chart-time-signal-strobe.type'
import type { IMeasurementResult } from './types/chart.type'

import { ChartWorkMode } from './chart.constant'

export interface IChartRange {
  rangeX: number[] | null
  rangeY: number[] | null
}

export interface IChartState {
  measurementResults: IMeasurementResult[]
  selectedResultId?: string
  selectedResultIndex: number
  chartCountResults: number
  chartAmplitudeScale: EAmplitudeScale | null
  chartFrequencyScale: EFrequencyScale | null
  chartTargetUnit: EUnitType | null
  chartStoredWorkMode: TChartWorkMode
  configChart: IConfigChart | null
  strobesResults: IStrobesResult[]
  isAppliedToAll: boolean
}

const initialState: IChartState = {
  measurementResults: [],
  selectedResultId: undefined,
  selectedResultIndex: 0,
  chartCountResults: 0,
  chartAmplitudeScale: null,
  chartFrequencyScale: null,
  chartTargetUnit: null,
  chartStoredWorkMode: ChartWorkMode.workers,
  configChart: null,
  strobesResults: [],
  isAppliedToAll: false
}

export const chartSlice = createSlice({
  name: 'chart',
  initialState,
  reducers: {
    setIsAppliedToAll(state, action: PayloadAction<boolean>) {
      state.isAppliedToAll = action.payload
    },

    setChartAmplitudeScale(state, action: PayloadAction<EAmplitudeScale>) {
      state.chartAmplitudeScale = action.payload
    },

    setChartFrequencyScale(state, action: PayloadAction<EFrequencyScale>) {
      state.chartFrequencyScale = action.payload
    },

    setChartTargetUnit(state, action: PayloadAction<EUnitType>) {
      state.chartTargetUnit = action.payload
    },

    setSelectedResultId(state, action: PayloadAction<string>) {
      state.selectedResultId = action.payload
    },

    setSelectedResultIndex(state, action: PayloadAction<number>) {
      state.selectedResultIndex = action.payload
    },

    setChartCountResults(state, action: PayloadAction<number>) {
      state.chartCountResults = action.payload
    },

    setChartWorkMode(state, action: PayloadAction<TChartWorkMode>) {
      state.chartStoredWorkMode = action.payload
    },

    setConfigChart(state, action: PayloadAction<IConfigChart | null>) {
      state.configChart = action.payload
    },

    setMeasurementResults(state, action: PayloadAction<IMeasurementResult[]>) {
      state.measurementResults = action.payload
    },

    updateStrobeResult(state, action: PayloadAction<IStrobesResult>) {
      const strobesResult = action.payload

      if (!state.strobesResults) {
        state.strobesResults = [strobesResult]
        return
      }

      const foundIndex = state.strobesResults.findIndex(
        (el) => el.id === strobesResult.id
      )

      if (foundIndex === -1) {
        state.strobesResults.push(strobesResult)
      } else {
        const isActive = state.strobesResults[foundIndex].isActivated

        state.strobesResults[foundIndex] = {
          ...strobesResult,
          isActivated: isActive
        }
      }
    },

    toggleStrobesForChart(state) {
      if (!state.strobesResults || !state.selectedResultId) return

      const selectedResult = state.strobesResults.find(
        (el) => el.id === state.selectedResultId
      )

      if (!selectedResult) return

      // если поля areStrobesActivated не было - присваеваем его как true, если было - всем в результатах делаем его как false
      if (selectedResult.isActivated) {
        state.strobesResults.forEach((el) => {
          el.isActivated = false
        })
      } else {
        selectedResult.isActivated = true
      }
    },

    setStrobeResults(state, action: PayloadAction<IStrobesResult[]>) {
      state.strobesResults = action.payload
    },

    resetStrobes(state) {
      state.strobesResults = []
    }
  }
})

export const chartReducer = chartSlice.reducer
export const useChartActions = () => useDispatchedActions(chartSlice.actions)
